import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef } from "react";

import formatPropertyName from "utils/column/formatPropertyName";

import { IGroupByListColumn } from "models";

interface UseResolvedDefaultFieldListArgs {
  rawList: string[];
  columnsToUse: IGroupByListColumn[];
  // True while columnsToUse has not finished loading (see useColumns/
  // useFacilityColumns). While true, rawList is returned unresolved so a
  // slow initial fetch isn't mistaken for missing fields.
  isLoading: boolean;
  // Second lookup for ids the live column list structurally cannot hold.
  // Shapefile fields are only materialized into columnsToUse while the user
  // has that shapefile open in group-by (ShapefileCategory), so without this
  // a perfectly live shapefile field reads as unresolved and drops the count
  // that gates the Add and delete buttons - which hid the delete button on
  // every row and made a stuck field unremovable (EVA-126). Pass
  // useResolveShapefileColumn.
  resolveFallbackColumn?: (property: string) => Nullable<IGroupByListColumn>;
  // Absence outranks both lookups, mirroring the render path in the settings
  // screens: a shapefile column materialized into columnsToUse by group-by is
  // not evicted on project switch, so a stale-but-absent field could
  // otherwise count as resolved here while rendering as an unavailable row -
  // hiding the Add button the count is supposed to gate. Pass
  // useIsShapefileFieldAbsentFromProject.
  isAbsentField?: (property: string) => boolean;
}

/**
 * Resolves a persisted list of field/tile property keys against the live
 * column list, filtering out ids that don't currently resolve to a column.
 *
 * This is intentionally READ-ONLY. An id can be absent from `columnsToUse`
 * for reasons other than the underlying field being deleted - e.g. project-
 * scoped fields disappear when no project (or a different project) is
 * selected (`selectedProjectIdForColumns` in groupBySlice), user-defined
 * fields disappear if an org disables `allowUserDefinedFields`, and
 * unpublished org fields depend on an async per-user feature flag fetch
 * that can race with this render. None of those mean the field was deleted,
 * so this hook must never auto-persist a "cleaned up" list - doing so would
 * silently and permanently delete a user's legitimate tile the first time
 * it's merely out of context.
 */
export default function useResolvedDefaultFieldList({
  rawList,
  columnsToUse,
  isLoading,
  resolveFallbackColumn,
  isAbsentField
}: UseResolvedDefaultFieldListArgs): string[] {
  const resolvedList = useMemo(() => {
    if (isLoading) return rawList;
    return rawList.filter((item) => {
      const propertyName = formatPropertyName(item);
      if (isAbsentField?.(propertyName)) return false;
      if (columnsToUse.find((c) => propertyName === c.property)) return true;
      return !!resolveFallbackColumn?.(propertyName);
    });
  }, [rawList, columnsToUse, isLoading, resolveFallbackColumn, isAbsentField]);

  // Observability only - no write. Helps confirm in the field whether
  // unresolved ids are truly deleted fields or just out-of-context ones.
  const loggedSignatureRef = useRef<Nullable<string>>(null);
  useEffect(() => {
    if (isLoading || resolvedList.length === rawList.length) return;

    const signature = JSON.stringify(rawList);
    if (loggedSignatureRef.current === signature) return;
    loggedSignatureRef.current = signature;

    Sentry.addBreadcrumb({
      message: "Default field list contains ids that don't resolve to a live column",
      level: "warning",
      category: "user_settings",
      data: {
        rawList,
        unresolvedCount: rawList.length - resolvedList.length
      }
    });
  }, [resolvedList, rawList, isLoading]);

  return resolvedList;
}
